import { useCallback, useRef } from 'react';
import { markLessonProgress } from '../queries/lessons';
import { ViewOrigin } from '../models/contentView';

/**
 * Espaçamento mínimo entre dois envios. Os players disparam progresso a cada
 * poucos segundos; sem isto seria uma requisição por tick.
 *
 * O backend deduplica os eventos do dia, então enviar demais não suja o log —
 * mas continua sendo tráfego à toa.
 */
const INTERVALO_ENTRE_ENVIOS_MS = 30000;

interface Params {
  /** Slug da aula. Sem ele não há o que reportar. */
  slug?: string;
  origin?: ViewOrigin;
  course?: string;
  program?: string;
}

/**
 * Devolve o callback que os players chamam com os segundos assistidos.
 *
 * O front não decide se a aula foi iniciada ou consumida: manda os segundos e o
 * backend resolve, com a duração cadastrada e o limiar. É o que permite ao
 * player novo, que só expõe `playedSeconds` e não conhece a duração, participar
 * do mesmo log que o legado.
 *
 * O callback é estável entre renders de propósito: o `Player` é memoizado e não
 * compara esta prop, então uma função nova a cada render ficaria para trás. Os
 * parâmetros vivem numa ref para que ele nunca leia valores velhos.
 */
export function useLessonProgressLog({ slug, origin, course, program }: Params) {
  const ultimoEnvio = useRef(0);
  const parametros = useRef({ slug, origin, course, program });

  parametros.current = { slug, origin, course, program };

  return useCallback((seconds: number) => {
    const atual = parametros.current;

    if (!atual.slug || !Number.isFinite(seconds) || seconds <= 0) {
      return;
    }

    const agora = Date.now();

    if (agora - ultimoEnvio.current < INTERVALO_ENTRE_ENVIOS_MS) {
      return;
    }

    ultimoEnvio.current = agora;

    markLessonProgress(atual.slug, {
      seconds,
      origin: atual.origin,
      course: atual.course,
      program: atual.program,
    });
  }, []);
}

export default useLessonProgressLog;
